import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, Link2, Phone, Reply, Variable } from "lucide-react";
import type {
  WaTemplateCategory,
  WaTemplateLanguage,
  WaTemplateMediaType,
  WaTemplateButton,
  WaTemplateButtonType,
} from "@/data/whatsappTemplateMocks";
import { ROLT_VARIABLES, LANGUAGE_OPTIONS } from "@/data/whatsappTemplateMocks";

interface TemplateFormData {
  name: string;
  category: WaTemplateCategory;
  language: WaTemplateLanguage;
  mediaType: WaTemplateMediaType;
  mediaPreviewUrl?: string;
  headerText?: string;
  body: string;
  footer?: string;
  buttons: WaTemplateButton[];
  /** Exemplo de cada variável posicional. A Meta exige para analisar o modelo. */
  bodyExamples: string[];
}

interface TemplateFormProps {
  data: TemplateFormData;
  onChange: (data: TemplateFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting?: boolean;
}

/** Nome de template na Meta: apenas minúsculas, números e underscore. */
const TEMPLATE_NAME_RE = /^[a-z0-9_]+$/;

/** Índices das variáveis posicionais presentes no texto, em ordem. */
const positionalVariables = (text: string): number[] => {
  const found = new Set<number>();
  for (const match of String(text ?? '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
    found.add(Number(match[1]));
  }
  return [...found].sort((a, b) => a - b);
};

export const TemplateForm = ({ data, onChange, onSubmit, onCancel, submitting = false }: TemplateFormProps) => {
  const { t } = useTranslation(["settings", "common"]);
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const update = (partial: Partial<TemplateFormData>) => {
    onChange({ ...data, ...partial });
  };

  // Regras que a Meta aplica na análise. Validar aqui evita descobrir o erro
  // só depois do POST — e no meio de uma gravação de tela.
  const bodyVariables = positionalVariables(data.body);
  const nameError =
    data.name && !TEMPLATE_NAME_RE.test(data.name)
      ? t("waTemplates.nameInvalid")
      : null;
  const missingExamples = bodyVariables.some(
    (_, position) => !(data.bodyExamples?.[position] ?? '').trim()
  );
  const canSubmit = Boolean(data.name) && Boolean(data.body) && !nameError && !missingExamples && !submitting;

  /**
   * Insere uma variável POSICIONAL ({{1}}, {{2}}...) — o único formato que a
   * Meta aceita neste modo. Os nomes do menu (Nome do contato, etc.) servem
   * apenas para pré-preencher o exemplo correspondente, que a Meta exige.
   *
   * Inserir o nome literal ({{contact_name}}) faria a Meta reprovar o modelo.
   */
  const insertVariable = (placeholder: string) => {
    const textarea = bodyRef.current;
    if (!textarea) return;

    const nextIndex = bodyVariables.length + 1;
    const token = `{{${nextIndex}}}`;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentBody = data.body;
    const newBody = currentBody.substring(0, start) + token + currentBody.substring(end);

    const nextExamples = [...(data.bodyExamples ?? [])];
    nextExamples[nextIndex - 1] = placeholder;

    update({ body: newBody, bodyExamples: nextExamples });
    setShowVariableMenu(false);

    setTimeout(() => {
      textarea.focus();
      const newPos = start + token.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const addButton = () => {
    if (data.buttons.length >= 3) return;
    const newButton: WaTemplateButton = {
      id: `btn_${Date.now()}`,
      type: 'QUICK_REPLY',
      text: '',
    };
    update({ buttons: [...data.buttons, newButton] });
  };

  const updateButton = (id: string, partial: Partial<WaTemplateButton>) => {
    update({
      buttons: data.buttons.map(b => (b.id === id ? { ...b, ...partial } : b)),
    });
  };

  const removeButton = (id: string) => {
    update({ buttons: data.buttons.filter(b => b.id !== id) });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    update({ mediaPreviewUrl: url });
  };

  const buttonTypeIcon = (type: WaTemplateButtonType) => {
    switch (type) {
      case 'QUICK_REPLY': return <Reply className="w-3.5 h-3.5" />;
      case 'URL': return <Link2 className="w-3.5 h-3.5" />;
      case 'PHONE_NUMBER': return <Phone className="w-3.5 h-3.5" />;
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-2">
          <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.name")}</Label>
          <Input
            value={data.name}
            onChange={e => update({ name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
            placeholder={t("waTemplates.namePlaceholder")}
            className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
          />
          {nameError ? (
            <span className="text-xs text-[#DC2626] dark:text-[#F87171]">{nameError}</span>
          ) : (
            <span className="text-xs text-[#64748B] dark:text-[#71717A]">{t("waTemplates.nameHint")}</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.type")}</Label>
          <Select value={data.category} onValueChange={v => update({ category: v as WaTemplateCategory })}>
            <SelectTrigger className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UTILITY">{t("waTemplates.typeUtility")}</SelectItem>
              <SelectItem value="MARKETING">Marketing</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.language")}</Label>
          <Select value={data.language} onValueChange={v => update({ language: v as WaTemplateLanguage })}>
            <SelectTrigger className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.mediaType")}</Label>
          <Select value={data.mediaType} onValueChange={v => update({ mediaType: v as WaTemplateMediaType, mediaPreviewUrl: undefined })}>
            <SelectTrigger className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("waTemplates.mediaNone")}</SelectItem>
              <SelectItem value="image">{t("waTemplates.mediaImage")}</SelectItem>
              <SelectItem value="video">{t("waTemplates.mediaVideo")}</SelectItem>
              <SelectItem value="document">{t("waTemplates.mediaDocument")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {data.mediaType !== 'none' && (
          <div className="flex flex-col gap-2">
            <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.mediaUpload")}</Label>
            <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[#E5E7EB] bg-[#F8FAFC] cursor-pointer hover:border-[#2563EB] hover:bg-[#EEF4FF] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#18181B] dark:hover:border-[#60A5FA] dark:hover:bg-blue-500/10">
              <Upload className="w-4 h-4 text-[#64748B] dark:text-[#A1A1AA]" />
              <span className="text-sm text-[#64748B] truncate dark:text-[#A1A1AA]">
                {data.mediaPreviewUrl ? t("waTemplates.fileSelected") : t("waTemplates.chooseFile")}
              </span>
              <input
                type="file"
                className="hidden"
                accept={data.mediaType === 'image' ? 'image/*' : data.mediaType === 'video' ? 'video/*' : '.pdf,.doc,.docx'}
                onChange={handleFileUpload}
              />
            </label>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.message")}</Label>
          <div className="relative">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs text-[#2563EB] hover:text-[#1D4ED8] gap-1 h-7 transition-colors duration-200 dark:text-[#60A5FA] dark:hover:text-[#93C5FD]"
              onClick={() => setShowVariableMenu(!showVariableMenu)}
            >
              <Variable className="w-3.5 h-3.5" />
              {t("waTemplates.insertVariable")}
            </Button>
            {showVariableMenu && (
              <div className="absolute right-0 top-full mt-1 w-64 rounded-xl border border-[#E5E7EB] bg-white shadow-md z-50 overflow-hidden dark:border-[#3F3F46] dark:bg-[#27272A] dark:shadow-none">
                <div className="p-2 border-b border-[#E5E7EB] dark:border-[#3F3F46]">
                  <p className="text-xs font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.roltVariables")}</p>
                </div>
                {ROLT_VARIABLES.map(v => (
                  <button
                    key={v.value}
                    onClick={() => insertVariable(v.placeholder)}
                    className="w-full text-left px-3 py-2 text-sm text-[#0F172A] hover:bg-[#EEF4FF] transition-colors duration-200 flex justify-between dark:text-[#FAFAFA] dark:hover:bg-blue-500/15"
                  >
                    <span>{v.label}</span>
                    <span className="font-mono text-xs text-[#64748B] dark:text-[#71717A]">
                      {`{{${bodyVariables.length + 1}}}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <Textarea
          ref={bodyRef}
          value={data.body}
          onChange={e => update({ body: e.target.value })}
          placeholder={t("waTemplates.bodyPlaceholder")}
          className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] min-h-[120px] resize-none"
          maxLength={1024}
        />
        <div className="flex justify-between">
          <span className="text-xs text-[#64748B] dark:text-[#71717A]">
            {t("waTemplates.variableHint")}
          </span>
          <span className="text-xs text-[#64748B] dark:text-[#71717A]">{data.body.length}/1024</span>
        </div>

        {bodyVariables.length > 0 && (
          <div className="mt-2 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-3 dark:border-[#3F3F46] dark:bg-[#18181B]">
            <p className="text-xs font-medium text-[#0F172A] mb-2 dark:text-[#FAFAFA]">
              {t("waTemplates.examplesTitle")}
            </p>
            <p className="text-xs text-[#64748B] mb-3 dark:text-[#71717A]">
              {t("waTemplates.examplesHint")}
            </p>
            <div className="flex flex-col gap-2">
              {bodyVariables.map((variableIndex, position) => (
                <div key={variableIndex} className="flex items-center gap-2">
                  <span className="font-mono text-xs text-[#64748B] w-12 shrink-0 dark:text-[#71717A]">
                    {`{{${variableIndex}}}`}
                  </span>
                  <Input
                    value={data.bodyExamples?.[position] ?? ''}
                    onChange={e => {
                      const next = [...(data.bodyExamples ?? [])];
                      next[position] = e.target.value;
                      update({ bodyExamples: next });
                    }}
                    placeholder={t("waTemplates.examplePlaceholder")}
                    className="h-9 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.footer")}</Label>
        <Input
          value={data.footer || ''}
          onChange={e => update({ footer: e.target.value })}
          placeholder={t("waTemplates.footerPlaceholder")}
          className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
          maxLength={60}
        />
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Label className="font-medium text-sm text-[#0F172A] dark:text-[#FAFAFA]">{t("waTemplates.buttons")}</Label>
          {data.buttons.length < 3 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs text-[#2563EB] hover:text-[#1D4ED8] gap-1 h-7 transition-colors duration-200 dark:text-[#60A5FA] dark:hover:text-[#93C5FD]"
              onClick={addButton}
            >
              <Plus className="w-3.5 h-3.5" />
              {t("waTemplates.addButton")}
            </Button>
          )}
        </div>

        {data.buttons.map((btn) => (
          <div key={btn.id} className="flex gap-2 items-start p-3 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-[#18181B]/50">
            <div className="flex flex-col gap-2 flex-1">
              <div className="flex gap-2">
                <Select value={btn.type} onValueChange={v => updateButton(btn.id, { type: v as WaTemplateButtonType })}>
                  <SelectTrigger className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] w-[160px]">
                    <div className="flex items-center gap-1.5">
                      {buttonTypeIcon(btn.type)}
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QUICK_REPLY">{t("waTemplates.btnQuickReply")}</SelectItem>
                    <SelectItem value="URL">{t("waTemplates.btnUrl")}</SelectItem>
                    <SelectItem value="PHONE_NUMBER">{t("waTemplates.btnPhone")}</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={btn.text}
                  onChange={e => updateButton(btn.id, { text: e.target.value })}
                  placeholder={t("waTemplates.buttonTextPlaceholder")}
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] flex-1"
                  maxLength={25}
                />
              </div>
              {btn.type === 'URL' && (
                <Input
                  value={btn.url || ''}
                  onChange={e => updateButton(btn.id, { url: e.target.value })}
                  placeholder="https://exemplo.com/pagina"
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
                />
              )}
              {btn.type === 'PHONE_NUMBER' && (
                <Input
                  value={btn.phoneNumber || ''}
                  onChange={e => updateButton(btn.id, { phoneNumber: e.target.value })}
                  placeholder="+55 11 99999-0000"
                  className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
                />
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-[#DC2626] hover:text-[#B91C1C] hover:bg-[#FEF2F2] transition-colors duration-200 dark:text-[#F87171] dark:hover:bg-red-500/10"
              onClick={() => removeButton(btn.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-3 justify-end pt-2 border-t border-[#E5E7EB] dark:border-[#3F3F46]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="min-w-[100px] rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
        >
          {t("common:actions.cancel")}
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          className="min-w-[160px] rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 disabled:opacity-60 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
          disabled={!canSubmit}
        >
          {submitting ? t("waTemplates.submitting") : t("waTemplates.submitForApproval")}
        </Button>
      </div>
    </div>
  );
};
